import {useState} from 'react'
import {useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {PageHeading} from '@/components/page-heading'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {HelpIcon} from '@/components/help-icon'
import {PorQue} from '@/components/ui/por-que'
import {FiltrosDeEstado, type OpcionEstado} from '@/components/ui/filtros-de-estado'
import {sgsstPageGuard} from '@/components/no-access-state'
import {CifraModal} from '@/components/cifra-modal'
import {CifraProcedencia} from '@/components/cifra-procedencia'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {VigenciaSveModal} from '@/components/vigencia-sve-modal'
import {useBusiness, useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useCifras, useVigenciasSve, type Cifra, type VigenciaSve} from '@/hooks/empresa/organizacion/use-cifras'
import {formatDate} from '@/lib/risk'

const ORIGEN_TONE: Record<string, 'amber' | 'neutral'> = {
  fijada_empresa: 'neutral',
  propuesto_kairos: 'neutral',
  sin_definir: 'amber',
}

/** Una sola decisión (E1 D-B): fijar una y no las otras deja la vigencia a medio definir. */
const TRIO_PERIODICIDAD = ['periodicidad_periodico_meses', 'periodicidad_desde', 'periodicidad_base']

/** `propuesto_kairos` no tiene chip: es el default del que se sale fijando. */
type CifrasFiltro = 'todas' | 'sin_definir' | 'fijadas'

function ValorCifra({cifra}: {cifra: Cifra}) {
  const {t} = useTranslation()
  const texto =
    cifra.valor_texto === null
      ? t('cifras.sinDefinirValor')
      : cifra.tipo === 'opcion'
        ? t(`cifras.opciones.${cifra.cifra_key}.${cifra.valor_texto}`)
        : `${cifra.valor_texto} ${t(`cifras.unidades.${cifra.cifra_key}`)}`
  return (
    <p className="font-mono text-sm text-foreground" data-testid="cifra-valor">
      {texto}
    </p>
  )
}

/** Deciden sobre datos de salud. Espejo de `core/cifras.py::CLAVES_DE_CUSTODIA`, que es quien las rechaza. */
const CLAVES_DE_CUSTODIA = ['mi_espacio_conceptos', 'copia_solicitudes_ips']

/** Nadie lee todavía `mi_espacio_conceptos` (P-E1-7): ofrecerla sin decirlo haría creer que apagarla protege algo. */
const SIN_SUPERFICIE = ['mi_espacio_conceptos']

function esDeCustodia(cifraKey: string): boolean {
  return CLAVES_DE_CUSTODIA.includes(cifraKey)
}

type CifrasTab = 'cifras' | 'vigencias'

export function CifrasEmpresaPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const tab: CifrasTab = searchParams.get('tab') === 'vigencias' ? 'vigencias' : 'cifras'
  function setTab(next: CifrasTab) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        return params
      },
      {replace: true}
    )
  }
  const {data: business, isLoading} = useBusiness(businessId)
  const capabilities = useCapabilities(businessId)
  const godReadOnly = useGodModeReadOnly()
  const {data, isError, refetch} = useCifras(capabilities.sgsst_operar ? businessId : undefined)
  const [cifrasFiltro, setCifrasFiltro] = useState<CifrasFiltro>('todas')
  const [editando, setEditando] = useState<Cifra | null>(null)
  const [vigenciaAbierta, setVigenciaAbierta] = useState<VigenciaSve | null>(null)
  const [vigenciaModal, setVigenciaModal] = useState(false)
  const [vigenciasPage, setVigenciasPage] = useState(1)
  const {data: vigencias} = useVigenciasSve(capabilities.sgsst_operar ? businessId : undefined, vigenciasPage)

  // Antes del guard: react-query se rinde en silencio tras dos reintentos y el esqueleto giraría para siempre.
  if (!isLoading && capabilities.sgsst_operar && isError) {
    return (
      <div className="space-y-3">
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="cifras-error">
          {t('cifras.errorCarga')}
        </p>
        <Button size="sm" variant="outline" onClick={() => refetch()}>
          {t('cifras.reintentar')}
        </Button>
      </div>
    )
  }
  const guard = sgsstPageGuard(isLoading, capabilities.sgsst_operar, business && data)
  if (guard) return guard

  const cifras = data!.cifras
  const puedeFijar = capabilities.sgsst_firmar && !godReadOnly
  // Dos bloqueos: un admin de Inci que además es responsable real sí puede firmar (`_admin_gana`);
  // lo que le falta es soltar el modo dios.
  const razonBloqueo = godReadOnly
    ? t(capabilities.sgsst_firmar ? 'cifras.bloqueoSeguro' : 'cifras.bloqueoModoDios')
    : capabilities.firmar_bloqueado_por
  // Las claves de custodia las fija quien custodia la salud, no cualquier firmante; la API responde 403.
  const puedeCustodiar = puedeFijar && capabilities.sgsst_custodiar_salud
  const sinDefinir = cifras.filter((cifra) => cifra.origen === 'sin_definir').length
  const fijadas = cifras.filter((cifra) => cifra.origen === 'fijada_empresa').length
  // Son doce y ya están en el navegador: el chip filtra en el cliente, sin paginación.
  const visibles =
    cifrasFiltro === 'sin_definir'
      ? cifras.filter((cifra) => cifra.origen === 'sin_definir')
      : cifrasFiltro === 'fijadas'
        ? cifras.filter((cifra) => cifra.origen === 'fijada_empresa')
        : cifras
  const opcionesCifras: OpcionEstado<CifrasFiltro>[] = [
    {valor: 'todas', etiqueta: t('cifras.filtro.todas'), conteo: cifras.length},
    {valor: 'sin_definir', etiqueta: t('cifras.origen.sin_definir'), conteo: sinDefinir},
    {valor: 'fijadas', etiqueta: t('cifras.filtro.fijadas'), conteo: fijadas},
  ]
  const trioFaltante = TRIO_PERIODICIDAD.filter(
    (clave) => cifras.find((cifra) => cifra.cifra_key === clave)?.origen !== 'fijada_empresa'
  )
  const trioAMedias = trioFaltante.length > 0 && trioFaltante.length < TRIO_PERIODICIDAD.length

  return (
    <div className="space-y-5" data-testid="cifras-empresa">
      <PageHeading
        title={t('cifras.title')}
        subtitle={business!.name}
        ayuda={
          <HelpIcon label={t('common.help.iconLabelFor', {campo: t('cifras.title')})} testId="cifras-intro">
            {t('cifras.intro')}
          </HelpIcon>
        }
      />

      {!puedeFijar && razonBloqueo && (
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="cifras-bloqueo">
          {razonBloqueo}
        </p>
      )}

      <Tabs value={tab} onValueChange={(v) => setTab(v as CifrasTab)}>
        <TabsList>
          <TabsTrigger value="cifras" data-testid="cifras-tab-cifras">
            {t('cifras.tabs.cifras')}
          </TabsTrigger>
          <TabsTrigger value="vigencias" data-testid="cifras-tab-vigencias">
            {t('cifras.tabs.vigencias')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cifras" className="space-y-5 pt-4">
          <div data-testid="cifras-resumen">
            <FiltrosDeEstado
              opciones={opcionesCifras}
              valor={cifrasFiltro}
              onValor={setCifrasFiltro}
              testIdPrefijo="cifras-filtro"
            />
          </div>

          <div className="surface divide-y divide-border rounded-xl">
            {visibles.map((cifra) => (
              <div
                key={cifra.cifra_key}
                data-testid={`cifra-fila-${cifra.cifra_key}`}
                className="flex flex-wrap items-start justify-between gap-3 px-4 py-3.5">
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{t(`cifras.claves.${cifra.cifra_key}.label`)}</span>
                    <HelpIcon
                      label={t('common.help.iconLabelFor', {campo: t(`cifras.claves.${cifra.cifra_key}.label`)})}
                      testId={`cifra-ayuda-${cifra.cifra_key}`}>
                      <p>{t(`cifras.claves.${cifra.cifra_key}.hint`)}</p>
                      {SIN_SUPERFICIE.includes(cifra.cifra_key) && (
                        <p className="mt-1.5" data-testid="cifra-sin-superficie">
                          {t('cifras.sinSuperficie')}
                        </p>
                      )}
                      {esDeCustodia(cifra.cifra_key) && puedeFijar && !puedeCustodiar && (
                        <p className="mt-1.5" data-testid="cifra-solo-custodia">
                          {t('cifras.soloCustodia')}
                        </p>
                      )}
                    </HelpIcon>
                    <Tag tone={ORIGEN_TONE[cifra.origen]}>{t(`cifras.origen.${cifra.origen}`)}</Tag>
                  </div>
                  <ValorCifra cifra={cifra} />
                  <CifraProcedencia cifra={cifra} />
                  {trioAMedias && TRIO_PERIODICIDAD.includes(cifra.cifra_key) && (
                    <p
                      className="notice-amber flex max-w-xl flex-wrap items-center gap-2 rounded-lg px-2 py-1 text-xs"
                      data-testid="cifra-trio-incompleto">
                      {t('cifras.trioIncompleto', {
                        faltan: trioFaltante.map((clave) => t(`cifras.claves.${clave}.label`)).join(', '),
                      })}
                      <PorQue
                        label={t('cifras.trioPorqueLabel')}
                        ariaLabel={t('cifras.trioPorqueAria', {campo: t(`cifras.claves.${cifra.cifra_key}.label`)})}
                        testId={`cifra-trio-porque-${cifra.cifra_key}`}>
                        {t('cifras.trioPorqueBody')}
                      </PorQue>
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`cifra-fijar-${cifra.cifra_key}`}
                  disabled={esDeCustodia(cifra.cifra_key) ? !puedeCustodiar : !puedeFijar}
                  title={
                    esDeCustodia(cifra.cifra_key) && puedeFijar && !puedeCustodiar
                      ? t('cifras.soloCustodia')
                      : puedeFijar
                        ? undefined
                        : (razonBloqueo ?? undefined)
                  }
                  onClick={() => setEditando(cifra)}>
                  {cifra.origen === 'fijada_empresa' ? t('cifras.cambiar') : t('cifras.fijar')}
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="vigencias" className="pt-4">
          <section className="space-y-2" data-testid="cifras-vigencias-sve">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="flex items-center gap-1.5 font-mono text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                {t('cifras.sve.title')}
                <HelpIcon
                  label={t('common.help.iconLabelFor', {campo: t('cifras.sve.title')})}
                  testId="cifras-sve-ayuda">
                  {t('cifras.sve.hint')}
                </HelpIcon>
              </h2>
              <Button
                size="sm"
                variant="outline"
                data-testid="cifras-sve-fijar"
                disabled={!puedeFijar}
                title={puedeFijar ? undefined : (razonBloqueo ?? undefined)}
                onClick={() => {
                  setVigenciaAbierta(null)
                  setVigenciaModal(true)
                }}>
                {t('cifras.sve.fijar')}
              </Button>
            </div>

            {!vigencias?.total ? (
              <p className="text-sm text-muted-foreground" data-testid="cifras-sve-vacio">
                {t('cifras.sve.vacio')}
              </p>
            ) : (
              <>
                <ul className="surface divide-y divide-border rounded-xl">
                  {vigencias.items.map((vigencia) => (
                    <li
                      key={vigencia.position_id}
                      className="flex flex-wrap items-start justify-between gap-3 px-4 py-3"
                      data-testid="cifras-sve-fila">
                      <div className="min-w-0 space-y-1">
                        <p className="font-mono text-sm">
                          {t('cifras.sve.meses', {
                            cargo: vigencia.position_nombre,
                            meses: vigencia.meses,
                            sve: vigencia.sve_nombre,
                          })}
                        </p>
                        {/* Van aparte: la constancia es el acto firmado y el alcance se recalcula al leer. */}
                        <p className="text-xs text-muted-foreground">
                          {t('cifras.sve.constancia', {
                            fecha: formatDate(vigencia.confirmado_en),
                            nombre: vigencia.confirmado_por_nombre ?? '',
                          })}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('cifras.sve.alcance', {examenes: vigencia.examenes_alcanzados})}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`cifras-sve-cambiar-${vigencia.position_id}`}
                        disabled={!puedeFijar}
                        title={puedeFijar ? undefined : (razonBloqueo ?? undefined)}
                        onClick={() => {
                          setVigenciaAbierta(vigencia)
                          setVigenciaModal(true)
                        }}>
                        {t('cifras.sve.cambiar')}
                      </Button>
                    </li>
                  ))}
                </ul>
                <PaginationBar
                  page={vigencias.page}
                  pageSize={vigencias.page_size}
                  total={vigencias.total}
                  onPageChange={setVigenciasPage}
                />
              </>
            )}
          </section>
        </TabsContent>
      </Tabs>

      <CifraModal
        open={editando !== null}
        onOpenChange={(open) => !open && setEditando(null)}
        businessId={businessId!}
        cifra={editando}
      />
      <VigenciaSveModal
        open={vigenciaModal}
        onOpenChange={setVigenciaModal}
        businessId={businessId!}
        vigencia={vigenciaAbierta}
        mesesMinimo={data!.sve_meses_minimo}
        mesesMaximo={data!.sve_meses_maximo}
      />
    </div>
  )
}
